/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
document.addEventListener('DOMContentLoaded', function () {
  const susoageServiceId = window.SUSAGE_CONFIG?.serviceId;
  let isAdjusting = false;

  // カート更新イベントでの調整
  document.addEventListener('cart:updated', function (event) {
    if (isAdjusting) return;

    const cart = event.detail.cart;
    if (cart) {
      checkAndAdjustSusoageService(cart, susoageServiceId);
    }
  });

  // 裾上げサービス調整のチェック関数
  function checkAndAdjustSusoageService(cart, serviceId) {
    if (!serviceId) {
      console.warn('裾上げサービスIDが設定されていません');
      return;
    }

    let susoagePropertyCount = 0;
    let susoageServiceCount = 0;
    const analysisDetails = [];

    cart.items.forEach((item, index) => {
      if (!item || !item.variant_id) return;

      const variantId = item.variant_id.toString();
      const isSusoageService = variantId === serviceId;
      const productTitle = item.product_title || item.title || 'Unknown';
      const quantity = item.quantity || 0;
      const properties = item.properties || {};

      if (isSusoageService) {
        susoageServiceCount += quantity;
        analysisDetails.push(`Item ${index + 1}: 裾上げサービス (数量: ${quantity})`);
      } else {
        // 裾上げプロパティをチェック（統一：'裾上げ'のみ）
        const hasSusoageProperty = properties['裾上げ'] && properties['裾上げ'] !== '';

        if (hasSusoageProperty) {
          susoagePropertyCount += quantity;
          const propertyValue = properties['裾上げ'];
          analysisDetails.push(
            `Item ${index + 1}: ${productTitle} - 裾上げあり (数量: ${quantity}, 値: ${propertyValue})`,
          );
        } else {
          analysisDetails.push(`Item ${index + 1}: ${productTitle} - 裾上げなし (数量: ${quantity})`);
        }
      }
    });

    if (susoagePropertyCount !== susoageServiceCount) {
      adjustSusoageService(susoagePropertyCount, serviceId);
    }
  }

  function adjustSusoageService(targetQuantity, serviceId) {
    if (isAdjusting) {
      return;
    }

    isAdjusting = true;

    const isMainCartPage = window.location.pathname === '/cart';
    const requestBody = {
      updates: {},
      attributes: {},
    };

    requestBody.updates[serviceId] = targetQuantity;

    // 属性も更新（テーマによってはこれが必要な場合がある）
    requestBody.attributes['裾上げサービス'] = targetQuantity > 0 ? targetQuantity.toString() : '';
    requestBody.attributes['option-product-costs'] = targetQuantity > 0 ? targetQuantity.toString() : '';

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        // 成功メッセージ
        let notificationMessage = '';
        if (targetQuantity > 0) {
          notificationMessage = `裾上げサービスを${targetQuantity}個に調整しました`;
        } else {
          notificationMessage = '裾上げサービスを削除しました';
        }

        if (isMainCartPage) {
          window.location.reload();
        } else {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { source: 'susoage-minicart-system' },
            }),
          );

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:updated', {
              bubbles: true,
              detail: {
                cart: data,
                source: 'susoage-minicart-system',
              },
            }),
          );

          // テーマ固有の更新処理
          if (window.theme && window.theme.cartDrawer && window.theme.cartDrawer.refresh) {
            window.theme.cartDrawer.refresh();
          }

          // Shopifyの標準的なカート更新も試行
          if (window.Shopify && window.Shopify.onCartUpdate) {
            window.Shopify.onCartUpdate(data);
          }

          // 通知表示（利用可能な場合）
          if (window.showToast || window.showNotification) {
            const showFunction = window.showToast || window.showNotification;
            showFunction(notificationMessage);
          }

          setTimeout(() => {
            isAdjusting = false;
          }, 300);
        }
      })
      .catch((error) => {
        console.error('ミニカート調整エラー:', error);
        isAdjusting = false;

        // エラー通知
        if (window.showToast || window.showNotification) {
          const showFunction = window.showToast || window.showNotification;
          showFunction('裾上げサービスの調整に失敗しました');
        }
      });
  }

  // デバッグ用関数をグローバルに公開
  window.debugMinicartSusoage = async function () {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      checkAndAdjustSusoageService(cart, susoageServiceId);
    } catch (error) {
      console.error('デバッグエラー:', error);
    }
  };
});

document.addEventListener('DOMContentLoaded', function () {
  const menuLink = document.querySelector('.js-account-menu-link');
  if (!menuLink) return;

  menuLink.addEventListener('click', function (e) {
    e.preventDefault();

    const targetElement = document.querySelector('.c_account-menu-wrapper');
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
  });
});

// リサイズ中はtransitionを無効化する
let resizeTimer;
window.addEventListener('resize', () => {
  document.body.classList.add('is-resizing');
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    document.body.classList.remove('is-resizing');
  }, 300);
});
